import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
// Import OStress 850 types for future use
// import { OStress850Configuration, OStressExecutionHandle } from "@/types/ostress-850-integration";
import { 
  Play, 
  Pause, 
  RotateCcw,
  AlertTriangle,
  TrendingDown,
  Database,
  Zap,
  Clock,
  Activity,
  FileText,
  Download,
  Target,
  BarChart3
} from "lucide-react";

interface StressTestScenario {
  id: string;
  name: string;
  description: string;
  category: 'Memory' | 'CPU' | 'IO' | 'Blocking' | 'Replication';
  breakdownPoint: {
    metric: string;
    threshold: number;
    unit: string;
  };
  automatedFix: {
    trigger: string;
    action: string;
    script: string;
  };
  simulationParams: {
    duration: number;
    intensity: number;
    concurrency: number;
  };
}

interface BreakdownResult {
  scenario: string;
  breakdownPoint: number;
  metricAtBreakdown: string;
  timeToBreakdown: number;
  recoveryTime: number;
  automatedFixTriggered: boolean;
  postFixPerformance: number;
}

export function StressTestLab() {
  const [selectedScenario, setSelectedScenario] = useState<string>("tempdb-contention");
  const [isRunning, setIsRunning] = useState(false);
  const [testIntensity, setTestIntensity] = useState([70]);
  const [maxConcurrency, setMaxConcurrency] = useState([100]);
  const [breakdownThreshold, setBreakdownThreshold] = useState([80]);
  
  // OStress 850 specific state
  const [ostressRunning, setOstressRunning] = useState(false);
  const [ostressConcurrency, setOstressConcurrency] = useState([50]);
  const [ostressDuration, setOstressDuration] = useState("300");
  const [ostressScenario, setOstressScenario] = useState("standard");
  const [ostressClientCode, setOstressClientCode] = useState("costco");
  
  const scenarios: StressTestScenario[] = [
    {
      id: "tempdb-contention",
      name: "TempDB Contention Stress Test",
      description: "Simulate heavy tempdb usage with allocation contention and space pressure until system breakdown",
      category: "Memory",
      breakdownPoint: {
        metric: "TempDB Space Usage",
        threshold: 95,
        unit: "%"
      },
      automatedFix: {
        trigger: "TempDB usage > 90%",
        action: "Add tempdb data files, clear temp objects",
        script: `-- Emergency TempDB expansion
ALTER DATABASE tempdb ADD FILE (
    NAME = 'tempdev_emergency',
    FILENAME = 'C:\\TempDB\\tempdev_emergency.ndf',
    SIZE = 1GB,
    FILEGROWTH = 512MB
);

-- Clear temp objects
DBCC FREEPROCCACHE;
DBCC FREESYSTEMCACHE('ALL');`
      },
      simulationParams: {
        duration: 300,
        intensity: 80,
        concurrency: 50
      }
    },
    {
      id: "parameter-sniffing",
      name: "Parameter Sniffing Cascade",
      description: "Generate bad query plans through parameter sniffing that progressively degrade performance",
      category: "CPU",
      breakdownPoint: {
        metric: "Query Duration",
        threshold: 30000,
        unit: "ms"
      },
      automatedFix: {
        trigger: "Avg query time > 10 seconds",
        action: "Force plan recompilation, update statistics",
        script: `-- Force plan recompilation
DBCC FREEPROCCACHE;

-- Update statistics with full scan
UPDATE STATISTICS dbo.LargeTable WITH FULLSCAN;

-- Enable query store for plan forcing
ALTER DATABASE CURRENT SET QUERY_STORE = ON;`
      },
      simulationParams: {
        duration: 180,
        intensity: 90,
        concurrency: 25
      }
    },
    {
      id: "memory-pressure",
      name: "Memory Pressure Escalation",
      description: "Gradually consume buffer pool memory until checkpoint stalls and query timeouts occur",
      category: "Memory",
      breakdownPoint: {
        metric: "Buffer Cache Hit Ratio",
        threshold: 85,
        unit: "%"
      },
      automatedFix: {
        trigger: "Buffer cache hit ratio < 90%",
        action: "Increase max server memory, clear ad-hoc cache",
        script: `-- Increase max server memory
EXEC sp_configure 'max server memory (MB)', 8192;
RECONFIGURE;

-- Clear ad-hoc query cache
DBCC FREESESSIONCACHE;
DBCC FREEPROCCACHE('ad hoc');`
      },
      simulationParams: {
        duration: 240,
        intensity: 85,
        concurrency: 40
      }
    },
    {
      id: "blocking-cascade",
      name: "Blocking Chain Cascade",
      description: "Create escalating blocking chains that lead to deadlocks and connection pool exhaustion",
      category: "Blocking",
      breakdownPoint: {
        metric: "Blocked Processes",
        threshold: 50,
        unit: "count"
      },
      automatedFix: {
        trigger: "Blocked processes > 20",
        action: "Kill blocking sessions, enable read committed snapshot",
        script: `-- Kill long-running blocking sessions
DECLARE @spid int;
SELECT @spid = session_id 
FROM sys.dm_exec_requests 
WHERE blocking_session_id = 0 
  AND wait_time > 30000;

IF @spid IS NOT NULL
    EXEC('KILL ' + @spid);

-- Enable read committed snapshot isolation
ALTER DATABASE CURRENT SET READ_COMMITTED_SNAPSHOT ON;`
      },
      simulationParams: {
        duration: 120,
        intensity: 95,
        concurrency: 75
      }
    },
    {
      id: "replication-lag",
      name: "Replication Lag Spiral",
      description: "Simulate replication lag that compounds until secondary replicas fall hours behind",
      category: "Replication",
      breakdownPoint: {
        metric: "Replication Lag",
        threshold: 300,
        unit: "seconds"
      },
      automatedFix: {
        trigger: "Replication lag > 60 seconds",
        action: "Failover to secondary, investigate network",
        script: `-- Emergency failover to secondary
ALTER AVAILABILITY GROUP [YourAG] 
FAILOVER TO 'SecondaryReplica';

-- Check replication health
SELECT 
    replica_server_name,
    last_commit_time,
    DATEDIFF(second, last_commit_time, GETUTCDATE()) as lag_seconds
FROM sys.dm_hadr_database_replica_states;`
      },
      simulationParams: {
        duration: 600,
        intensity: 60,
        concurrency: 30
      }
    }
  ];

  const [testResults, setTestResults] = useState<BreakdownResult[]>([
    {
      scenario: "TempDB Contention Stress Test",
      breakdownPoint: 94.2,
      metricAtBreakdown: "TempDB Space Usage: 94.2%",
      timeToBreakdown: 156,
      recoveryTime: 23,
      automatedFixTriggered: true,
      postFixPerformance: 78
    },
    {
      scenario: "Parameter Sniffing Cascade",
      breakdownPoint: 28500,
      metricAtBreakdown: "Query Duration: 28.5 seconds", 
      timeToBreakdown: 89,
      recoveryTime: 45,
      automatedFixTriggered: true,
      postFixPerformance: 85
    }
  ]);

  const runStressTest = () => {
    setIsRunning(true);
    const scenario = scenarios.find(s => s.id === selectedScenario);
    
    // Simulate stress test running
    setTimeout(() => {
      setIsRunning(false);
      
      // Add new result
      const newResult: BreakdownResult = {
        scenario: scenario?.name || "Unknown",
        breakdownPoint: scenario?.breakdownPoint.threshold! * (0.95 + Math.random() * 0.1),
        metricAtBreakdown: `${scenario?.breakdownPoint.metric}: ${(scenario?.breakdownPoint.threshold! * (0.95 + Math.random() * 0.1)).toFixed(1)} ${scenario?.breakdownPoint.unit}`,
        timeToBreakdown: Math.floor(scenario?.simulationParams.duration! * (0.3 + Math.random() * 0.5)),
        recoveryTime: Math.floor(20 + Math.random() * 40),
        automatedFixTriggered: Math.random() > 0.2,
        postFixPerformance: Math.floor(75 + Math.random() * 20)
      };
      
      setTestResults(prev => [newResult, ...prev.slice(0, 4)]);
    }, 8000);
  };

  const selectedScenarioData = scenarios.find(s => s.id === selectedScenario);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            SQL Server Stress Testing & Breakdown Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="scenarios" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="scenarios">Stress Scenarios</TabsTrigger>
              <TabsTrigger value="configuration">Test Configuration</TabsTrigger>
              <TabsTrigger value="ostress-850">OStress 850 Testing</TabsTrigger>
              <TabsTrigger value="results">Breakdown Results</TabsTrigger>
              <TabsTrigger value="automation">Remediation Scripts</TabsTrigger>
            </TabsList>

            <TabsContent value="scenarios" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {scenarios.map((scenario) => (
                  <Card key={scenario.id} className={`p-4 cursor-pointer border-2 ${
                    selectedScenario === scenario.id ? 'border-shiftleft-500 bg-shiftleft-50' : 'border-gray-200'
                  }`} onClick={() => setSelectedScenario(scenario.id)}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{scenario.name}</h4>
                          <Badge variant="outline">{scenario.category}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{scenario.description}</p>
                        
                        <div className="space-y-2">
                          <div className="p-2 bg-red-50 border border-red-200 rounded text-sm">
                            <div className="font-medium text-red-800">Breakdown Point</div>
                            <div className="text-red-600">
                              {scenario.breakdownPoint.metric} &gt; {scenario.breakdownPoint.threshold}{scenario.breakdownPoint.unit}
                            </div>
                          </div>
                          
                          <div className="p-2 bg-green-50 border border-green-200 rounded text-sm">
                            <div className="font-medium text-green-800">Auto-Fix Trigger</div>
                            <div className="text-green-600">{scenario.automatedFix.trigger}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="configuration" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-4">
                  <h4 className="font-semibold mb-4">Test Parameters</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Selected Scenario</label>
                      <Select value={selectedScenario} onValueChange={setSelectedScenario}>
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {scenarios.map(scenario => (
                            <SelectItem key={scenario.id} value={scenario.id}>
                              {scenario.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Test Intensity</label>
                      <Slider
                        value={testIntensity}
                        onValueChange={setTestIntensity}
                        max={100}
                        min={10}
                        step={5}
                        className="mt-2"
                      />
                      <div className="text-xs text-gray-500 mt-1">{testIntensity[0]}% intensity</div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Max Concurrency</label>
                      <Slider
                        value={maxConcurrency}
                        onValueChange={setMaxConcurrency}
                        max={200}
                        min={10}
                        step={10}
                        className="mt-2"
                      />
                      <div className="text-xs text-gray-500 mt-1">{maxConcurrency[0]} concurrent connections</div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Breakdown Threshold</label>
                      <Slider
                        value={breakdownThreshold}
                        onValueChange={setBreakdownThreshold}
                        max={95}
                        min={50}
                        step={5}
                        className="mt-2"
                      />
                      <div className="text-xs text-gray-500 mt-1">{breakdownThreshold[0]}% of normal capacity</div>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-semibold mb-4">Scenario Details</h4>
                  
                  {selectedScenarioData && (
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm font-medium">Duration</div>
                        <div className="text-sm text-gray-600">{selectedScenarioData.simulationParams.duration} seconds</div>
                      </div>
                      
                      <div>
                        <div className="text-sm font-medium">Target Breakdown</div>
                        <div className="text-sm text-gray-600">
                          {selectedScenarioData.breakdownPoint.metric} exceeds {selectedScenarioData.breakdownPoint.threshold}{selectedScenarioData.breakdownPoint.unit}
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-sm font-medium">Auto-Remediation</div>
                        <div className="text-sm text-gray-600">{selectedScenarioData.automatedFix.action}</div>
                      </div>
                      
                      <div className="pt-3">
                        <Button 
                          onClick={runStressTest}
                          disabled={isRunning}
                          className={`w-full ${isRunning ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'} text-white`}
                        >
                          {isRunning ? (
                            <>
                              <Activity className="w-4 h-4 mr-2 animate-spin" />
                              Running Stress Test...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Start Stress Test
                            </>
                          )}
                        </Button>
                        
                        {isRunning && (
                          <div className="mt-3 text-center">
                            <div className="text-sm text-orange-600 font-medium">
                              Pushing system to breakdown point...
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div className="bg-orange-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="ostress-850" className="space-y-4">
              <div className="space-y-6">
                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Database className="w-6 h-6 text-shiftleft-600" />
                    <div>
                      <h3 className="text-lg font-semibold">OStress 850 Import Testing</h3>
                      <p className="text-sm text-gray-600">
                        Execute concurrent stress tests against usp_al_import_850 with real-time performance monitoring
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Test Configuration */}
                    <Card className="p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Test Configuration
                      </h4>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Concurrency Level</label>
                          <Slider
                            value={ostressConcurrency}
                            onValueChange={setOstressConcurrency}
                            max={200}
                            min={1}
                            step={1}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>1</span>
                            <span>{ostressConcurrency[0]} concurrent executions</span>
                            <span>200</span>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Test Duration</label>
                          <Select value={ostressDuration} onValueChange={setOstressDuration}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="60">1 minute</SelectItem>
                              <SelectItem value="300">5 minutes</SelectItem>
                              <SelectItem value="600">10 minutes</SelectItem>
                              <SelectItem value="1800">30 minutes</SelectItem>
                              <SelectItem value="3600">1 hour</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">EDI 850 Scenario</label>
                          <Select value={ostressScenario} onValueChange={setOstressScenario}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="standard">Standard Orders</SelectItem>
                              <SelectItem value="return">Return Orders</SelectItem>
                              <SelectItem value="rats">RATS Orders</SelectItem>
                              <SelectItem value="back_to_store">Back to Store</SelectItem>
                              <SelectItem value="mixed">Mixed Scenarios</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Client Codes</label>
                          <Select value={ostressClientCode} onValueChange={setOstressClientCode}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="costco">Costco</SelectItem>
                              <SelectItem value="amazon">Amazon</SelectItem>
                              <SelectItem value="wayfair">Wayfair</SelectItem>
                              <SelectItem value="all">All Clients</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </Card>
                    
                    {/* Performance Monitoring */}
                    <Card className="p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Performance Monitoring
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-shiftleft-50 rounded-lg">
                            <div className="text-sm font-medium text-shiftleft-800">CPU Usage</div>
                            <div className="text-2xl font-bold text-shiftleft-600">--</div>
                            <div className="text-xs text-shiftleft-600">Waiting for test...</div>
                          </div>
                          
                          <div className="p-3 bg-green-50 rounded-lg">
                            <div className="text-sm font-medium text-green-800">Memory Usage</div>
                            <div className="text-2xl font-bold text-green-600">--</div>
                            <div className="text-xs text-green-600">Waiting for test...</div>
                          </div>
                          
                          <div className="p-3 bg-orange-50 rounded-lg">
                            <div className="text-sm font-medium text-orange-800">I/O IOPS</div>
                            <div className="text-2xl font-bold text-orange-600">--</div>
                            <div className="text-xs text-orange-600">Waiting for test...</div>
                          </div>
                          
                          <div className="p-3 bg-purple-50 rounded-lg">
                            <div className="text-sm font-medium text-purple-800">TempDB Usage</div>
                            <div className="text-2xl font-bold text-purple-600">--</div>
                            <div className="text-xs text-purple-600">Waiting for test...</div>
                          </div>
                        </div>
                        
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-medium text-gray-800 mb-2">Execution Statistics</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>Executions/sec: <span className="font-medium">--</span></div>
                            <div>Avg Response: <span className="font-medium">--</span></div>
                            <div>Success Rate: <span className="font-medium">--</span></div>
                            <div>Active Connections: <span className="font-medium">--</span></div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                  
                  {/* Control Buttons */}
                  <div className="flex items-center gap-3 mt-6 pt-4 border-t">
                    <Button 
                      className="flex items-center gap-2" 
                      disabled={ostressRunning}
                      onClick={() => {
                        setOstressRunning(true);
                        // TODO: Implement actual OStress execution
                        console.log('Starting OStress test with config:', {
                          concurrency: ostressConcurrency[0],
                          duration: ostressDuration,
                          scenario: ostressScenario,
                          clientCode: ostressClientCode
                        });
                      }}
                    >
                      <Play className="w-4 h-4" />
                      {ostressRunning ? 'Running...' : 'Start OStress Test'}
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2" 
                      disabled={!ostressRunning}
                      onClick={() => {
                        // TODO: Implement pause functionality
                        console.log('Pausing OStress test');
                      }}
                    >
                      <Pause className="w-4 h-4" />
                      Pause
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2" 
                      disabled={!ostressRunning}
                      onClick={() => {
                        setOstressRunning(false);
                        // TODO: Implement stop functionality
                        console.log('Stopping OStress test');
                      }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      Stop
                    </Button>
                    
                    <Button 
                      variant="destructive" 
                      className="flex items-center gap-2 ml-auto" 
                      disabled={!ostressRunning}
                      onClick={() => {
                        setOstressRunning(false);
                        // TODO: Implement emergency stop functionality
                        console.log('Emergency stop activated');
                      }}
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Emergency Stop
                    </Button>
                  </div>
                  
                  {/* Safety Status */}
                  <div className={`mt-4 p-3 rounded-lg ${
                    ostressRunning 
                      ? 'bg-yellow-50 border border-yellow-200' 
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        ostressRunning ? 'bg-yellow-500' : 'bg-green-500'
                      }`}></div>
                      <span className={`text-sm font-medium ${
                        ostressRunning ? 'text-yellow-800' : 'text-green-800'
                      }`}>
                        {ostressRunning ? 'OStress Test Running' : 'Safety Systems Active'}
                      </span>
                    </div>
                    <div className={`text-xs mt-1 ${
                      ostressRunning ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {ostressRunning 
                        ? `Running ${ostressConcurrency[0]} concurrent executions • Duration: ${Math.floor(parseInt(ostressDuration) / 60)}min • Scenario: ${ostressScenario}`
                        : 'Emergency stop triggers enabled • Resource monitoring active • Max concurrency: 200'
                      }
                    </div>
                  </div>
                </Card>
                
                {/* Results Preview */}
                <Card className="p-6">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Recent Test Results
                  </h4>
                  
                  <div className="text-center py-8 text-gray-500">
                    <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No OStress test results yet</p>
                    <p className="text-sm">Start a test to see performance metrics and analysis</p>
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="results" className="space-y-4">
              <div className="space-y-4">
                {testResults.map((result, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        {result.scenario}
                      </h4>
                      <div className="flex gap-2">
                        <Badge className={result.automatedFixTriggered ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {result.automatedFixTriggered ? 'Auto-Fix Applied' : 'Manual Intervention'}
                        </Badge>
                        <Badge variant="outline">{result.timeToBreakdown}s to breakdown</Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-red-600">{result.breakdownPoint.toFixed(1)}</div>
                        <div className="text-xs text-gray-500">Breakdown Value</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold">{result.timeToBreakdown}s</div>
                        <div className="text-xs text-gray-500">Time to Breakdown</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-shiftleft-600">{result.recoveryTime}s</div>
                        <div className="text-xs text-gray-500">Recovery Time</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{result.postFixPerformance}%</div>
                        <div className="text-xs text-gray-500">Post-Fix Performance</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold">{result.metricAtBreakdown.split(':')[1]?.trim() || 'N/A'}</div>
                        <div className="text-xs text-gray-500">Peak Value</div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <div className="text-sm">
                        <span className="font-medium">Breakdown Metric:</span> {result.metricAtBreakdown}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="automation" className="space-y-4">
              <div className="space-y-4">
                {scenarios.map((scenario) => (
                  <Card key={scenario.id} className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold">{scenario.name}</h4>
                        <div className="text-sm text-gray-600">{scenario.automatedFix.action}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-1" />
                          Export
                        </Button>
                        <Button size="sm">
                          <Zap className="w-4 h-4 mr-1" />
                          Deploy
                        </Button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="text-sm font-medium mb-1">Trigger Condition</div>
                      <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                        {scenario.automatedFix.trigger}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium mb-1">Remediation Script</div>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
                        {scenario.automatedFix.script}
                      </pre>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}